import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from './useDebounce';

// Types for speaker duplicates
export interface SpeakerDuplicate {
  speaker_id: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  email: string | null;
  workplace: string | null;
  speaker_position: string | null;
  photo_url: string | null;
  bio: string | null;
  additional_info: string | null;
  date_of_birth: string | null;
  education: string | null;
  academic_degree: string | null;
  academic_title: string | null;
  similarity_score: number;
  match_reason: 'email_exact' | 'name_high' | 'name_medium' | 'name_low';
}

// Types for organizer duplicates
export interface OrganizerDuplicate {
  organizer_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  image_url: string | null;
  description: string | null;
  website_url: string | null;
  facebook_url: string | null;
  similarity_score: number;
  match_reason: 'email_exact' | 'name_high' | 'name_medium' | 'name_low';
}

// Types for partner duplicates
export interface PartnerDuplicate {
  partner_id: string;
  name: string;
  logo_url: string | null;
  additional_info: string | null;
  event_id: string;
  similarity_score: number;
  match_reason: 'name_exact' | 'name_high' | 'name_medium' | 'name_low';
}

export type Confidence = 'high' | 'medium' | 'low' | 'none';

interface DuplicateCheckResult<T> {
  duplicates: T[];
  confidence: Confidence;
  count: number;
}

interface UseSpeakerDuplicateDetectionParams {
  lastName: string;
  firstName: string;
  middleName?: string | null;
  email?: string | null;
  excludeId?: string | null;
  enabled?: boolean;
  debounceMs?: number;
}

interface UseOrganizerDuplicateDetectionParams {
  name: string;
  email?: string | null;
  excludeId?: string | null;
  enabled?: boolean;
  debounceMs?: number;
}

interface UsePartnerDuplicateDetectionParams {
  name: string;
  eventId?: string | null;
  excludeId?: string | null;
  enabled?: boolean;
  debounceMs?: number;
}

interface DuplicateDetectionState<T> {
  duplicates: T[];
  confidence: Confidence;
  isLoading: boolean;
  error: string | null;
  dismiss: () => void;
  isDismissed: boolean;
  refresh: () => void;
}

/**
 * Hook for detecting duplicate speakers based on name similarity
 */
export function useSpeakerDuplicateDetection({
  lastName,
  firstName,
  middleName,
  email,
  excludeId,
  enabled = true,
  debounceMs = 500,
}: UseSpeakerDuplicateDetectionParams): DuplicateDetectionState<SpeakerDuplicate> {
  const [duplicates, setDuplicates] = useState<SpeakerDuplicate[]>([]);
  const [confidence, setConfidence] = useState<Confidence>('none');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  // Refs to track current (non-debounced) values – prevents stale debounced
  // values from triggering API calls when the form is reset/cleared
  const lastNameRef = useRef(lastName);
  lastNameRef.current = lastName;
  const firstNameRef = useRef(firstName);
  firstNameRef.current = firstName;

  // Debounce the search values
  const debouncedLastName = useDebounce(lastName, debounceMs);
  const debouncedFirstName = useDebounce(firstName, debounceMs);
  const debouncedEmail = useDebounce(email || '', debounceMs);

  const checkDuplicates = useCallback(async () => {
    // Don't check if disabled or current names are too short (ref check prevents stale debounce)
    if (!enabled || !lastNameRef.current || lastNameRef.current.length < 2 || !firstNameRef.current || firstNameRef.current.length < 2 || !debouncedLastName || debouncedLastName.length < 2 || !debouncedFirstName || debouncedFirstName.length < 2) {
      setDuplicates([]);
      setConfidence('none');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/speakers/check-duplicates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lastName: debouncedLastName,
          firstName: debouncedFirstName,
          middleName: middleName || null,
          email: debouncedEmail || null,
          excludeId: excludeId || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check for duplicates');
      }

      const data: DuplicateCheckResult<SpeakerDuplicate> = await response.json();
      setDuplicates(data.duplicates);
      setConfidence(data.confidence);
      setIsDismissed(false); // Reset dismissed state when new results come in
    } catch (err) {
      console.error('Error checking speaker duplicates:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setDuplicates([]);
      setConfidence('none');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedLastName, debouncedFirstName, middleName, debouncedEmail, excludeId, enabled]);

  useEffect(() => {
    checkDuplicates();
  }, [checkDuplicates]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  const refresh = useCallback(() => {
    setIsDismissed(false);
    checkDuplicates();
  }, [checkDuplicates]);

  return {
    duplicates,
    confidence,
    isLoading,
    error,
    dismiss,
    isDismissed,
    refresh,
  };
}

/**
 * Hook for detecting duplicate organizers based on name similarity
 */
export function useOrganizerDuplicateDetection({
  name,
  email,
  excludeId,
  enabled = true,
  debounceMs = 500,
}: UseOrganizerDuplicateDetectionParams): DuplicateDetectionState<OrganizerDuplicate> {
  const [duplicates, setDuplicates] = useState<OrganizerDuplicate[]>([]);
  const [confidence, setConfidence] = useState<Confidence>('none');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  // Ref to track current (non-debounced) name – prevents stale debounced
  // values from triggering API calls when the form is reset/cleared
  const nameRef = useRef(name);
  nameRef.current = name;

  // Debounce the search values
  const debouncedName = useDebounce(name, debounceMs);
  const debouncedEmail = useDebounce(email || '', debounceMs);

  const checkDuplicates = useCallback(async () => {
    // Don't check if disabled or current name is too short (ref check prevents stale debounce)
    if (!enabled || !nameRef.current || nameRef.current.length < 3 || !debouncedName || debouncedName.length < 3) {
      setDuplicates([]);
      setConfidence('none');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/organizers/check-duplicates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: debouncedName,
          email: debouncedEmail || null,
          excludeId: excludeId || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check for duplicates');
      }

      const data: DuplicateCheckResult<OrganizerDuplicate> = await response.json();
      setDuplicates(data.duplicates);
      setConfidence(data.confidence);
      setIsDismissed(false);
    } catch (err) {
      console.error('Error checking organizer duplicates:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setDuplicates([]);
      setConfidence('none');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedName, debouncedEmail, excludeId, enabled]);

  useEffect(() => {
    checkDuplicates();
  }, [checkDuplicates]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  const refresh = useCallback(() => {
    setIsDismissed(false);
    checkDuplicates();
  }, [checkDuplicates]);

  return {
    duplicates,
    confidence,
    isLoading,
    error,
    dismiss,
    isDismissed,
    refresh,
  };
}

/**
 * Hook for detecting duplicate partners based on name similarity
 */
export function usePartnerDuplicateDetection({
  name,
  eventId,
  excludeId,
  enabled = true,
  debounceMs = 500,
}: UsePartnerDuplicateDetectionParams): DuplicateDetectionState<PartnerDuplicate> {
  const [duplicates, setDuplicates] = useState<PartnerDuplicate[]>([]);
  const [confidence, setConfidence] = useState<Confidence>('none');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  // Ref to track current (non-debounced) name – prevents stale debounced
  // values from triggering API calls when the form is reset/cleared
  const partnerNameRef = useRef(name);
  partnerNameRef.current = name;

  // Debounce the search values
  const debouncedName = useDebounce(name, debounceMs);

  const checkDuplicates = useCallback(async () => {
    // Don't check if disabled or current name is too short (ref check prevents stale debounce)
    if (!enabled || !partnerNameRef.current || partnerNameRef.current.length < 3 || !debouncedName || debouncedName.length < 3) {
      setDuplicates([]);
      setConfidence('none');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/partners/check-duplicates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: debouncedName,
          eventId: eventId || null,
          excludeId: excludeId || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check for duplicates');
      }

      const data: DuplicateCheckResult<PartnerDuplicate> = await response.json();
      setDuplicates(data.duplicates);
      setConfidence(data.confidence);
      setIsDismissed(false);
    } catch (err) {
      console.error('Error checking partner duplicates:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setDuplicates([]);
      setConfidence('none');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedName, eventId, excludeId, enabled]);

  useEffect(() => {
    checkDuplicates();
  }, [checkDuplicates]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  const refresh = useCallback(() => {
    setIsDismissed(false);
    checkDuplicates();
  }, [checkDuplicates]);

  return {
    duplicates,
    confidence,
    isLoading,
    error,
    dismiss,
    isDismissed,
    refresh,
  };
}
